import { createBlock } from './blockUtils';
import { parseVideoUrl } from './videoEmbed';

/** Max size for doc video uploads (matches mission attachments). */
export const DOC_VIDEO_MAX_BYTES = 100 * 1024 * 1024;

export const DOC_VIDEO_ALLOWED_TYPES = ['video/mp4', 'video/webm'];

/**
 * @param {File} file
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export const validateDocVideoFile = (file) => {
  if (!file) return { ok: false, error: 'No file selected' };
  if (!DOC_VIDEO_ALLOWED_TYPES.includes(file.type)) {
    return { ok: false, error: 'Use MP4 or WebM (max 100MB)' };
  }
  if (file.size > DOC_VIDEO_MAX_BYTES) {
    const mb = Math.round(file.size / (1024 * 1024));
    return { ok: false, error: `Video too large (${mb}MB). Maximum is 100MB` };
  }
  return { ok: true };
};

/** Empty video block — chooser, or embed URL prompt for a provider shortcut. */
export const createVideoBlock = ({ mode = 'chooser', provider = null } = {}) =>
  createBlock('video', '', {
    mode,
    provider: provider || null,
  });

/** Embed block from a successfully parsed share URL. */
export const createVideoEmbedBlock = (parsed) =>
  createBlock('video', '', {
    mode: 'embed',
    provider: parsed.provider,
    originalUrl: parsed.originalUrl,
    embedUrl: parsed.embedUrl,
    videoId: parsed.videoId,
    widthPercent: 100,
  });

/**
 * Build a video block with a temporary local preview; caller should kick off
 * upload via startDocVideoUpload(block.id, file).
 * @param {File} file
 */
export const createLocalVideoBlock = (file) => {
  const check = validateDocVideoFile(file);
  if (!check.ok) {
    return createBlock('video', '', {
      mode: 'upload',
      uploadStatus: 'error',
      errorMessage: check.error,
      fileName: file?.name || '',
    });
  }
  return createBlock('video', '', {
    mode: 'upload',
    localPreviewUrl: URL.createObjectURL(file),
    fileName: file.name,
    mimeType: file.type,
    size: file.size,
    uploadStatus: 'local',
    widthPercent: 100,
  });
};

/**
 * Open a hidden file picker for a single video.
 * @returns {Promise<File|null>}
 */
export const pickDocVideoFile = () =>
  new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = DOC_VIDEO_ALLOWED_TYPES.join(',');
    input.style.display = 'none';
    const cleanup = () => {
      input.remove();
    };
    input.addEventListener('change', () => {
      const file = input.files?.[0] || null;
      cleanup();
      resolve(file);
    });
    const onFocus = () => {
      setTimeout(() => {
        if (!input.files?.length) {
          cleanup();
          resolve(null);
        }
      }, 300);
      window.removeEventListener('focus', onFocus);
    };
    window.addEventListener('focus', onFocus);
    document.body.appendChild(input);
    input.click();
  });

/**
 * Collect video File objects from a paste or drop dataTransfer.
 * @param {DataTransfer|null|undefined} data
 * @returns {File[]}
 */
export const videoFilesFromDataTransfer = (data) => {
  if (!data) return [];
  const fromFiles = [...(data.files || [])].filter((f) =>
    DOC_VIDEO_ALLOWED_TYPES.includes(f.type)
  );
  if (fromFiles.length) return fromFiles;

  const fromItems = [];
  for (const item of data.items || []) {
    if (item.kind === 'file' && DOC_VIDEO_ALLOWED_TYPES.includes(item.type)) {
      const file = item.getAsFile();
      if (file) fromItems.push(file);
    }
  }
  return fromItems;
};

/**
 * Strip blob URLs / unfinished upload fields before persisting.
 */
export const sanitizeVideoBlockProps = (props = {}) => {
  const next = { ...props };
  delete next.localPreviewUrl;
  if (next.mode === 'embed' && next.embedUrl) {
    const parsed = next.originalUrl ? parseVideoUrl(next.originalUrl) : null;
    if (parsed) {
      next.embedUrl = parsed.embedUrl;
      next.provider = parsed.provider;
      next.videoId = parsed.videoId;
    }
    return next;
  }
  if (next.publicUrl) {
    next.mode = 'upload';
    next.uploadStatus = 'ready';
    delete next.errorMessage;
    return next;
  }
  if (
    next.uploadStatus === 'stub' ||
    next.uploadStatus === 'local' ||
    next.uploadStatus === 'uploading'
  ) {
    next.uploadStatus = 'error';
    next.errorMessage =
      next.errorMessage || 'Video upload was interrupted — try uploading again';
  }
  return next;
};
